import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import moment from 'moment';
import { DISABLE_PASSWORD_VALIDATION } from '../../config/constants';

export class Validation {
  static required = (value) => {
    if (!value) {
      return Messages.FIELD_IS_REQUIRED;
    }
    return false;
  };

  static requiredWithoutCaption = (value) => {
    if (!value) {
      return true;
    }
    return false;
  };

  static requiredRadioButton = (value) => {
    if (typeof value === 'undefined') {
      return Messages.FIELD_IS_REQUIRED;
    }
    return false;
  };

  static requireRadioButtonWithValue = (value) => {
    if (value === 999) {
      return Messages.FIELD_IS_REQUIRED;
    }
    return false;
  };

  static requiredCheckbox = (values, customMessage) => {
    if (
      !(values || []).length ||
      values.every((value) => value.value === false)
    )
      return customMessage || Messages.FIELD_IS_REQUIRED;
    return false;
  };

  static minMax = (value, isRequired = false, min, max, fieldName) => {
    if (!value && isRequired) {
      return Messages.FIELD_IS_REQUIRED;
    }

    if (value && (value.length < min || value.length > max)) {
      return `${fieldName} should include min ${min} max ${max} characters`;
    }

    return false;
  };

  static minMaxValue = (value, isRequired = false, min, max, fieldName) => {
    if (!value && isRequired) {
      return Messages.FIELD_IS_REQUIRED;
    }

    if (value && (value < min || value > max)) {
      return `${fieldName} should include min ${min} max ${max} characters`;
    }

    return false;
  };

  static minMaxValueBetween = (
    value,
    isRequired = false,
    min,
    max,
    fieldName,
  ) => {
    if (!value && isRequired) {
      return Messages.FIELD_IS_REQUIRED;
    }

    if (value && (value < min || value > max)) {
      return `${fieldName} must be between ${min} and ${max}`;
    }

    return false;
  };

  static maxL = (values, max, fieldName) => {
    if (values.length > max) {
      return `The length of ${fieldName} must be ${max} characters or fewer. You entered ${values.length} characters`;
    }
    return false;
  };

  static minL = (values, isRequired = false, min, fieldName) => {
    if (!values && isRequired) {
      return Messages.FIELD_IS_REQUIRED;
    }

    if (values.length < min) {
      return `The length of ${fieldName} must be ${min} characters. You entered ${values.length} characters`;
    }
    return false;
  };

  static image = (value, isRequired = false) => {
    if (!value && isRequired) {
      return {
        error: Messages.FIELD_IS_REQUIRED,
        hasFormUncompletedFiles: false,
      };
    }

    if (value && value.status === 'ERROR') {
      return { error: value.errorMessage, hasFormUncompletedFiles: false };
    }

    if (value && value.status === 'LOAD') {
      return { error: false, hasFormUncompletedFiles: true };
    }

    return { error: false, hasFormUncompletedFiles: false };
  };

  static files = (value, isRequired = false, maxFilesCount) => {
    if ((!value || !value.length) && isRequired) {
      return {
        error: Messages.FIELD_IS_REQUIRED,
        hasFormUncompletedFiles: false,
      };
    }

    if (value && value.length > maxFilesCount) {
      return {
        error: `You can upload up to ${maxFilesCount} files`,
        hasFormUncompletedFiles: false,
      };
    }

    const hasFormUncompletedFiles = Boolean(
      (value || []).find((file) => file.status && file.status !== 'COMPLETE'),
    );
    const hasFormErrorFiles = Boolean(
      (value || []).find((file) => file.status === 'ERROR'),
    );

    return { error: false, hasFormUncompletedFiles, hasFormErrorFiles };
  };

  static email = (value, isRequired = false) => {
    if (!value && !isRequired) {
      return false;
    }
    if (!value && isRequired) {
      return Messages.FIELD_IS_REQUIRED;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9]+([.-][A-Z]+)+$/i.test(value)) {
      return 'Ooops. Check that email format.';
    } else if (value.length > 45) {
      return 'Email should include max 45 characters';
    }
    return false;
  };
  // test
  static emailWithoutCaption = (value, isRequired = false) => {
    if (!value && !isRequired) {
      return false;
    }
    if (!value && isRequired) {
      return true;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9]+([.-][A-Z]+)+$/i.test(value)) {
      return 'Ooops. Check that email format.';
    } else if (value.length > 45) {
      return 'Email should include max 45 characters';
    }
    return false;
  };

  static phone = ({ phone, countryCode }, isRequired = false) => {
    if (!phone && isRequired) {
      return Messages.FIELD_IS_REQUIRED;
    } else if (phone) {
      const phoneNumber = parsePhoneNumberFromString(phone, countryCode);
      if (
        !phoneNumber ||
        (phoneNumber &&
          (!phoneNumber.isValid() || phoneNumber.number !== phone))
      ) {
        return 'Phone number is invalid';
      }
    }

    return false;
  };

  static phoneWithoutCaption = ({ phone, countryCode }, isRequired = false) => {
    if (!phone && isRequired) {
      return true;
    } else if (phone) {
      const phoneNumber = parsePhoneNumberFromString(phone, countryCode);
      if (
        !phoneNumber ||
        (phoneNumber &&
          (!phoneNumber.isValid() || phoneNumber.number !== phone))
      ) {
        return 'Phone number is invalid';
      }
    }

    return false;
  };

  static password = (value) => {
    if (DISABLE_PASSWORD_VALIDATION) {
      // If passwor validation disabled by APP config then fallback to simple required check
      return Validation.required(value);
    }

    if (!value) {
      return Messages.FIELD_IS_REQUIRED;
    }
    if (value.length < 8 || value.length > 64) {
      return 'Password should include min 8 max 64 characters';
    }

    // Check for invalid symbols first
    if (
      !/^[A-Za-z0-9 @#$%^&*\-_'!+=\[\]{}|\\:,'\.\?\/`~"();<>]*$/.test(value)
    ) {
      return 'Password should not include incorrect symbols';
    }

    // Allowed characters: uppercase, lowercase, digits, exact symbol list, and spaces
    const categories = [
      /[A-Z]/.test(value),
      /[a-z]/.test(value),
      /[0-9]/.test(value),
      /[ @#$%^&*\-_'!+=\[\]{}|\\:,'\.\?\/`~"();<>]/.test(value),
    ];

    if (categories.filter(Boolean).length < 3) {
      return `Password should include 3 of: uppercase, lowercase, number, symbol (@#$%^&-!+=[]{}|\\:;',.?/\`~\"()<> )`;
    }
    return false;
  };

  static number = (value) => {
    if (!value) {
      return Messages.FIELD_IS_REQUIRED;
    } else if (!/^[0-9]+$/.test(value)) {
      return 'Only numbers allowed';
    }
    return false;
  };

  static numberWithoutRequired = (value) => {
    if (!value) {
      return false;
    } else if (!/^[0-9]+$/.test(value)) {
      return 'Only numbers allowed';
    }
    return false;
  };

  static numberLetters = (value) => {
    if (!/^[A-Za-z0-9]*$/.test(value)) {
      return 'Only numbers and latin letters allowed';
    }
    return false;
  };

  static postalCode = (value, isRequired = false) => {
    if (!value && isRequired) {
      return Messages.FIELD_IS_REQUIRED;
    } else if (value && (value.length < 3 || value.length > 10)) {
      return 'Postal code should include min 3 max 10 characters';
    } else if (value && !/^[A-Za-z0-9\-\s]*$/.test(value)) {
      return '\tPostal code can include only digits, latin letters, spaces and hyphens';
    }
    return false;
  };

  static beforeDate = (value, thisDate, fieldName, secondFieldName) => {
    if (value && moment(value).isBefore(thisDate)) {
      return `${fieldName} cannot be earlier than ${
        secondFieldName ? secondFieldName : 'current date'
      }`;
    }

    return false;
  };
  static afterDate = (value, thisDate, fieldName, isRequired = false) => {
    if (!value && isRequired) {
      return Messages.FIELD_IS_REQUIRED;
    }

    if (value && moment(value).isAfter(thisDate)) {
      return `${fieldName} cannot be later than current date`;
    }
    return false;
  };

  static extWithoutRequired = (value) => {
    if (!value) {
      return false;
    } else if (!/^[0-9]+$/.test(value)) {
      return 'Ext. can include only digits';
    }
    return false;
  };

  static locationAddress = (
    values = {},
    requiredSettings = {
      phoneNumberRequired: false,
      streetNameRequired: true,
      cityRequired: true,
      countryCodeRequired: true,
      stateOrProvinceRequired: true,
      postalCodeRequired: true,
    },
  ) => {
    let validationResult;
    let errors = {};
    const validateValues = values || {};

    validationResult = Validation.required(validateValues.streetName);
    if (validationResult && requiredSettings.streetNameRequired) {
      errors.streetName = validationResult;
      errors._error = true;
    }

    validationResult = Validation.required(validateValues.city);
    if (validationResult && requiredSettings.cityRequired) {
      errors.city = validationResult;
      errors._error = true;
    }

    validationResult = Validation.required(validateValues.countryCode);
    if (validationResult && requiredSettings.countryCodeRequired) {
      errors.countryCode = validationResult;
      errors._error = true;
    } else if (validateValues.countryCode === 'US') {
      validationResult = Validation.required(validateValues.stateOrProvince);
      if (validationResult && requiredSettings.stateOrProvinceRequired) {
        errors.stateOrProvince = validationResult;
        errors._error = true;
      }
    }

    validationResult = Validation.phone(
      { phone: validateValues.phone, countryCode: validateValues.countryCode },
      requiredSettings.phoneNumberRequired,
    );
    if (validationResult) {
      errors.phone = validationResult;
      errors._error = true;
    }

    validationResult = Validation.postalCode(
      validateValues.postalCode,
      requiredSettings.postalCodeRequired,
    );
    if (validationResult) {
      errors.postalCode = validationResult;
      errors._error = true;
    }

    return errors;
  };

  static locationAddressExt = (
    values = {},
    requiredSettings = {
      phoneNumberRequired: false,
      streetNameRequired: true,
      cityRequired: true,
      countryCodeRequired: true,
      stateOrProvinceRequired: true,
      postalCodeRequired: true,
      // extRequired: false,
    },
  ) => {
    let validationResult;
    let errors = {};
    const validateValues = values || {};

    validationResult = Validation.required(validateValues.streetName);
    if (validationResult && requiredSettings.streetNameRequired) {
      errors.streetName = validationResult;
      errors._error = true;
    }

    validationResult = Validation.required(validateValues.city);
    if (validationResult && requiredSettings.cityRequired) {
      errors.city = validationResult;
      errors._error = true;
    }

    validationResult = Validation.extWithoutRequired(values.ext);
    if (validationResult && !requiredSettings.extRequired) {
      errors.ext = validationResult;
      errors._error = true;
    }

    validationResult = Validation.required(validateValues.countryCode);
    if (validationResult && requiredSettings.countryCodeRequired) {
      errors.countryCode = validationResult;
      errors._error = true;
    } else if (validateValues.countryCode === 'US') {
      validationResult = Validation.required(validateValues.stateOrProvince);
      if (validationResult && requiredSettings.stateOrProvinceRequired) {
        errors.stateOrProvince = validationResult;
        errors._error = true;
      }
    }

    validationResult = Validation.phone(
      { phone: validateValues.phone, countryCode: validateValues.countryCode },
      requiredSettings.phoneNumberRequired,
    );
    if (validationResult) {
      errors.phone = validationResult;
      errors._error = true;
    }

    validationResult = Validation.postalCode(validateValues.postalCode, true);
    if (validationResult && requiredSettings.postalCodeRequired) {
      errors.postalCode = validationResult;
      errors._error = true;
    }

    return errors;
  };

  static contacts = (
    values = {},
    requiredSettings = {
      firstNameRequired: true,
      lastNameRequired: true,
      countryCodeRequired: true,
      phoneNumberRequired: true,
      // extRequired: false,
      emailRequired: true,
    },
  ) => {
    let validationResult;
    let errors = {};

    validationResult = Validation.required(values.firstName);
    if (validationResult && requiredSettings.firstNameRequired) {
      errors.firstName = validationResult;
      errors._error = true;
    }

    validationResult = Validation.required(values.lastName);
    if (validationResult && requiredSettings.lastNameRequired) {
      errors.lastName = validationResult;
      errors._error = true;
    }

    validationResult = Validation.required(values.countryCode);
    if (validationResult && requiredSettings.countryCodeRequired) {
      errors.countryCode = validationResult;
      errors._error = true;
    }

    validationResult = Validation.phone(
      { phone: values.phoneNumber, countryCode: values.countryCode },
      requiredSettings.phoneNumberRequired,
    );
    if (validationResult) {
      errors.phoneNumber = validationResult;
      errors._error = true;
    }

    validationResult = Validation.extWithoutRequired(values.ext);
    if (validationResult && !requiredSettings.extRequired) {
      errors.ext = validationResult;
      errors._error = true;
    }

    validationResult = Validation.email(
      values.email,
      requiredSettings.emailRequired,
    );
    if (validationResult) {
      errors.email = validationResult;
      errors._error = true;
    }

    return errors;
  };

  static contactsWithGlobalError = (
    values = {},
    requiredSettings = {
      firstNameRequired: true,
      lastNameRequired: true,
      countryCodeRequired: true,
      phoneNumberRequired: true,
      // extRequired: false,
      emailRequired: true,
    },
  ) => {
    let validationResult;
    let errors = {};

    validationResult = Validation.requiredWithoutCaption(values.firstName);
    if (validationResult && requiredSettings.firstNameRequired) {
      errors.firstName = validationResult;
      errors._error = true;
    }

    validationResult = Validation.requiredWithoutCaption(values.lastName);
    if (validationResult && requiredSettings.lastNameRequired) {
      errors.lastName = validationResult;
      errors._error = true;
    }

    validationResult = Validation.requiredWithoutCaption(values.countryCode);
    if (validationResult && requiredSettings.countryCodeRequired) {
      errors.countryCode = validationResult;
      errors._error = true;
    }

    validationResult = Validation.phoneWithoutCaption(
      { phone: values.phoneNumber, countryCode: values.countryCode },
      requiredSettings.phoneNumberRequired,
    );
    if (validationResult) {
      errors.phoneNumber = validationResult;
      errors._error = true;
    }

    validationResult = Validation.extWithoutRequired(values.ext);
    if (validationResult && !requiredSettings.extRequired) {
      errors.ext = validationResult;
      errors._error = true;
    }

    validationResult = Validation.emailWithoutCaption(
      values.email,
      requiredSettings.emailRequired,
    );
    if (validationResult) {
      errors.email = validationResult;
      errors._error = true;
    }

    return errors;
  };

  static official = (
    values = {},
    requiredSettings = {
      emailRequired: true,
      phoneNumberRequired: true,
      countryCodeRequired: true,
      firstNameRequired: true,
      lastNameRequired: true,
      roleRequired: true,
      affiliationRequired: true,
    },
  ) => {
    let validationResult;
    let errors = {};

    validationResult = Validation.required(values.countryCode);
    if (validationResult && requiredSettings.countryCodeRequired) {
      errors.countryCode = validationResult;
      errors._error = true;
    }

    validationResult = Validation.phone(
      { phone: values.phoneNumber, countryCode: values.countryCode },
      requiredSettings.phoneNumberRequired,
    );
    if (validationResult) {
      errors.phoneNumber = validationResult;
      errors._error = true;
    }

    validationResult = Validation.email(
      values.email,
      requiredSettings.emailRequired,
    );
    if (validationResult) {
      errors.email = validationResult;
      errors._error = true;
    }

    validationResult = Validation.required(values.firstName);
    if (validationResult && requiredSettings.firstNameRequired) {
      errors.firstName = validationResult;
      errors._error = true;
    }

    validationResult = Validation.required(values.lastName);
    if (validationResult && requiredSettings.lastNameRequired) {
      errors.lastName = validationResult;
      errors._error = true;
    }

    validationResult = Validation.required(values.role);
    if (validationResult && requiredSettings.roleRequired) {
      errors.role = validationResult;
      errors._error = true;
    }

    validationResult = Validation.required(values.affiliation);
    if (validationResult && requiredSettings.affiliationRequired) {
      errors.affiliation = validationResult;
      errors._error = true;
    }

    return errors;
  };

  static contactsCompanyManager = (
    values = {},
    requiredSettings = {
      companyNameRequired: true,
      personNameRequired: true,
      countryCodeRequired: true,
      phoneNumberRequired: true,
      emailRequired: true,
    },
  ) => {
    let validationResult;
    let errors = {};

    validationResult = Validation.required(values.companyName);
    if (validationResult && requiredSettings.companyNameRequired) {
      errors.companyName = validationResult;
      errors._error = true;
    }

    validationResult = Validation.required(values.personName);
    if (validationResult && requiredSettings.personNameRequired) {
      errors.personName = validationResult;
      errors._error = true;
    }

    validationResult = Validation.required(values.countryCode);
    if (validationResult && requiredSettings.countryCodeRequired) {
      errors.countryCode = validationResult;
      errors._error = true;
    }

    validationResult = Validation.phone(
      { phone: values.phoneNumber, countryCode: values.countryCode },
      requiredSettings.phoneNumberRequired,
    );
    if (validationResult) {
      errors.phoneNumber = validationResult;
      errors._error = true;
    }

    validationResult = Validation.email(
      values.email,
      requiredSettings.emailRequired,
    );
    if (validationResult) {
      errors.email = validationResult;
      errors._error = true;
    }

    return errors;
  };

  static freezers = (values = {}, freezerFor) => {
    let validationResult;
    let errors = {
      [`freezers${freezerFor}Storage`]: {},
      [`secureRefrigerator${freezerFor}Storage`]: {},
    };

    if (values[`freezers${freezerFor}Storage`]) {
      validationResult = Validation.requiredRadioButton(
        values[`freezers${freezerFor}Storage`][`freezers${freezerFor}Storage`],
      );

      if (validationResult)
        errors[`freezers${freezerFor}Storage`][`freezers${freezerFor}Storage`] =
          validationResult;

      if (values[`freezers${freezerFor}Storage`]) {
        if (
          values[`freezers${freezerFor}Storage`][
            `freezers${freezerFor}Storage`
          ] === true
        ) {
          validationResult = Validation.requiredCheckbox(
            values[`freezers${freezerFor}Storage`].freezerTemperatures,
          );

          if (validationResult) {
            errors[`freezers${freezerFor}Storage`].freezerTemperatures =
              validationResult;
            errors._error = true;
          }
        }
      }
    }

    if (values[`secureFreezer${freezerFor}Storage`]) {
      validationResult = Validation.requiredRadioButton(
        values[`secureFreezer${freezerFor}Storage`][
          `secureFreezer${freezerFor}Storage`
        ],
      );

      if (validationResult)
        errors[`secureFreezer${freezerFor}Storage`][
          `secureFreezer${freezerFor}Storage`
        ] = validationResult;

      if (values[`secureFreezer${freezerFor}Storage`]) {
        if (
          values[`secureFreezer${freezerFor}Storage`][
            `secureFreezer${freezerFor}Storage`
          ] === true
        ) {
          validationResult = Validation.requiredRadioButton(
            values[`secureFreezer${freezerFor}Storage`].temperatureMonitored,
          );
          if (validationResult) {
            errors[`secureFreezer${freezerFor}Storage`] = {};
            errors[`secureFreezer${freezerFor}Storage`].temperatureMonitored =
              validationResult;
            errors._error = true;
          }
        }
      }
    }

    if (values[`refrigerator${freezerFor}Storage`]) {
      if (
        values[`refrigerator${freezerFor}Storage`][
          `refrigerator${freezerFor}Storage`
        ] === true
      ) {
        validationResult = Validation.requiredRadioButton(
          values[`refrigerator${freezerFor}Storage`][
            `refrigerator${freezerFor}Storage`
          ],
        );
        if (validationResult) {
          errors[`refrigerator${freezerFor}Storage`][
            `refrigerator${freezerFor}Storage`
          ] = validationResult;
          errors._error = true;
        }
      }
    }

    if (freezerFor !== 'Drug') {
      if (values[`secureRefrigerator${freezerFor}Storage`]) {
        validationResult = Validation.requiredRadioButton(
          values[`secureRefrigerator${freezerFor}Storage`][
            `secureRefrigerator${freezerFor}Storage`
          ],
        );

        if (validationResult)
          errors[`secureRefrigerator${freezerFor}Storage`][
            `secureRefrigerator${freezerFor}Storage`
          ] = validationResult;

        if (values[`secureRefrigerator${freezerFor}Storage`]) {
          if (
            values[`secureRefrigerator${freezerFor}Storage`][
              `secureRefrigerator${freezerFor}Storage`
            ] === true
          ) {
            validationResult = Validation.requiredRadioButton(
              values[`secureRefrigerator${freezerFor}Storage`]
                .temperatureMonitored,
            );
            if (validationResult) {
              errors[
                `secureRefrigerator${freezerFor}Storage`
              ].temperatureMonitored = validationResult;
              errors._error = true;
            }
          }
        }
      }
    }
    if (values[`secureFreezer${freezerFor}Storage`]) {
      if (
        values[`secureFreezer${freezerFor}Storage`][
          `secureFreezer${freezerFor}Storage`
        ] === true
      ) {
        validationResult = Validation.requiredRadioButton(
          values[`secureFreezer${freezerFor}Storage`].temperatureMonitored,
        );
        if (validationResult) {
          errors[`secureFreezer${freezerFor}Storage`].temperatureMonitored =
            validationResult;
          errors._error = true;
        }
      }
    }

    return errors;
  };
}

export const formSectionValidate = (values = {}, radioButtonFieldName) => {
  const errors = {};

  let validationResult = Validation.requiredRadioButton(
    values[radioButtonFieldName],
  );

  if (validationResult) {
    errors[radioButtonFieldName] = validationResult;
  }

  return Object.keys(errors).length ? errors : null;
};

export const Messages = {
  FIELD_IS_REQUIRED: 'Field is required!',
  FILL_IN_ALL: '*Please, fill in all required fields.',
  WAIT_FILES_LOADING: 'Please, wait until all files will be loaded.',
  WAIT_IMAGE_LOADING: 'Please wait until image has loaded.',
  FORM_HAS_ERROR_FILES: 'Some files uploaded with error',
};
